<template>
  <div class="tenancy-info-wrapper">
    <div class="company-info-sec">
      <div class="row">
        <div
          class="company-info input-set small-3 medium-3 large-3 columns"
          :class="{'required-error-highlight': inputRequireHighlight && !currentTenancy.companyId && !isDummyCompany}"
        >
          <label
            :class="'emphasis'"
            class="required-flag"
          >
            {{ $t('tenancy.companyId') }}
          </label>
          <div class="search__company search__company--id">
            <HKLSelect
              v-hkldinput
              class="seacrh__company-selector"
              :disabled="!canChangeCompanyNameAndID && (isDummyCompany || (!isCreate))"
              :value="companyIdDisplayValue"
              :options="companyIdHklOptions"
              :showSearch="true"
              :dropdownMatchSelectWidth="true"
              :showArrow="false"
              :openOnFocus="true"
              :filterOption="false"
              :hideNoDataDropdown="true"
              placeholder=""
              @focus="() => searchCompanies({ id: '' })"
              @search="val => searchCompanies({ id: val })"
              @change="selectCompanyId"
            />
            <span
              v-show="isDummyCompany ? false : !currentTenancy.companyId"
              class="fa fa-search"
            />
          </div>
        </div>

        <div
          class="company-info input-set small-5 medium-5 large-5 columns"
          :class="{'required-error-highlight': inputRequireHighlight && !deep(currentTenancy, 'company.name')}"
        >
          <label
            :class="'emphasis'"
            class="required-flag"
          >
            {{ $t('tenancy.companyName') }}
          </label>
          <div class="search__company search__company--name">
            <HKLSelect
              v-hkldinput
              class="seacrh__company-selector"
              :disabled="!canChangeCompanyNameAndID && (!isCreate && !isDummyCompany && !currentTenancy.companyChangeNotSaved) || disableUpdate"
              :value="companyName || undefined"
              :options="companyNameHklOptions"
              :showSearch="true"
              :dropdownMatchSelectWidth="true"
              :showArrow="false"
              :singleTaggableInput="true"
              placeholder=""
              @search="onCompanyNameSearch"
              @input="onCompanyNameInput"
              @change="onCompanyNameChange"
            />
            <span
              v-show="!companyName"
              class="fa fa-search"
            />
          </div>
        </div>

        <div class="company-info small-3 medium-3 large-3 columns">
          <label class="emphasis required-flag">{{ $t('tenancy.tradeName') }}</label>
          <HKLSelect
            v-hkldinput
            class="seacrh__tradeName-selector"
            :class="{'error-highlight': inputRequireHighlight && !currentTenancy.tradeName}"
            :disabled="disableUpdate"
            :value="currentTenancy.tradeName || undefined"
            :options="tradeNameHklOptions"
            :showSearch="true"
            :dropdownMatchSelectWidth="true"
            :showArrow="false"
            :singleTaggableInput="true"
            :openOnFocus="true"
            :hideNoDataDropdown="true"
            placeholder=""
            @input="onTradeNameInput"
            @change="onTradeNameChange"
          />
        </div>
      </div>

      <div class="row">
        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.generalLine') }}</label>
          <div class="generalline">
            <input
              v-hkldinput
              type="text"
              :disabled="true"
              :value="deep(currentTenancy, 'company.phone') || (isDummyCompany ? '' : '--')"
              @change="event => updateCompanyPathField('phone', event.target.value)"
            >
          </div>
        </div>
        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.alternativePhoneNumber') }}</label>
          <input
            v-hkldinput
            type="text"
            :disabled="true"
            :value="deep(currentTenancy, 'company.altPhone') || (isDummyCompany ? '' : '--')"
            @change="event => updateCompanyPathField('altPhone', event.target.value)"
          >
        </div>
        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.faxNumber') }}</label>
          <input
            v-hkldinput
            type="text"
            :disabled="true"
            :value="deep(currentTenancy, 'company.fax') || (isDummyCompany ? '' : '--')"
            @change="event => updateCompanyPathField('fax', event.target.value)"
          >
        </div>
      </div>

      <div class="row">
        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.email') }}</label>
          <input
            v-hkldinput
            type="text"
            :disabled="true"
            :value="deep(currentTenancy, 'company.email') || (isDummyCompany ? '' : '--')"
            @change="event => updateCompanyPathField('email', event.target.value)"
          >
        </div>
      </div>
    </div>

    <div class="premises-info-sec">
      <div
        v-if="!currentTenancy.id"
        class="row"
      >
        <div class="input-set main__contact-name small-3 medium-3 large-3 columns">
          <label class="emphasis required-flag">{{ $t('tenancy.mainContact') }} </label>
          <div class="row">
            <input
              v-for="fullnameObj of getFullnameSorts"
              :key="fullnameObj.key"
              v-hkldinput
              class="small-6 medium-6 large-6 columns"
              :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
              type="text"
              :value="currentTenancy[fullnameObj.value]"
              :class="{'error-highlight': inputRequireHighlight && !currentTenancy[fullnameObj.value]}"
              @change="event => updatePathField(fullnameObj.value, event.target.value)"
            >
          </div>
        </div>

        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.phoneNumber') }} </label>
          <input
            v-hkldinput
            type="text"
            :value="currentTenancy.phone"
            :class="{'error-highlight': inputRequireHighlight && isSamePhoneNumbers()}"
            @change="event => updatePathField('phone', event.target.value)"
          >
        </div>

        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.alternativePhoneNumber') }}</label>
          <input
            v-hkldinput
            type="text"
            :class="{'error-highlight': inputRequireHighlight && isSamePhoneNumbers()}"
            :value="currentTenancy.altPhone"
            @change="event => updatePathField('altPhone', event.target.value)"
          >
        </div>

        <div class="main__contact-email small-3 medium-3 large-3 columns">
          <label class="emphasis required-flag">{{ $t('user.email') }}</label>
          <!-- TODO <sam_liang> The tooltip doesn't work -->
          <input
            v-hkldinput
            v-fusioninputcheck="{
              type: 'string',
              pattern: regex.email,
              classes: 'tooltip-class',
              tip: tips.email,
              required: true
            }"
            type="email"
            :class="{'error-highlight': inputRequireHighlight && !currentTenancy.email}"
            :value="currentTenancy.email"
            @change="event => updatePathField('email', event.target.value)"
          >
        </div>
      </div>

      <div
        v-else-if="adminAccount && adminAccount.TenancyAccount"
        class="row"
      >
        <div class="input-set main__contact-name small-3 medium-3 large-3 columns">
          <label class="emphasis required-flag">{{ $t('tenancy.mainContact') }}</label>
          <div class="row">
            <input
              v-for="fullnameObj of getFullnameSorts"
              :key="fullnameObj.key"
              v-hkldinput
              class="small-6 medium-6 large-6 columns"
              type="text"
              :class="{'error-highlight': inputRequireHighlight && !adminAccount.TenancyAccount[`contact${fullnameObj.key}`]}"
              :disabled="disableUpdateMasterAccount"
              :value="adminAccount.TenancyAccount[`contact${fullnameObj.key}`]"
              @change="event => {
                adminAccount.TenancyAccount[`contact${fullnameObj.key}`] = event.target.value
                updateAdminAccount()
              }"
            >
          </div>
        </div>

        <div class="input-set small-3 medium-3 large-3 columns">
          <label class="emphasis">{{ $t('tenancy.phoneNumber') }}</label>
          <input
            v-hkldinput
            type="text"
            :class="{'error-highlight': inputRequireHighlight && isSamePhoneNumbers()}"
            :disabled="disableUpdate"
            :value="adminAccount.TenancyAccount.phone"
            @change="event => {
              adminAccount.TenancyAccount.phone = event.target.value
              updateAdminAccount()
            }"
          >
        </div>

        <div class="input-set small-3 medium-3 large-3 columns">
          <label :class="'emphasis'">{{ $t('tenancy.alternativePhoneNumber') }}</label>
          <input
            v-hkldinput
            type="text"
            :class="{'error-highlight': inputRequireHighlight && isSamePhoneNumbers()}"
            :disabled="disableUpdate"
            :value="adminAccount.TenancyAccount.altPhone"
            @change="event => {
              adminAccount.TenancyAccount.altPhone = event.target.value
              updateAdminAccount()
            }"
          >
        </div>

        <div class="main__contact-email small-3 medium-3 large-3 columns">
          <label class="emphasis required-flag">{{ $t('user.email') }}</label>
          <input
            v-hkldinput
            v-fusioninputcheck="{
              type: 'string',
              pattern: regex.email,
              classes: 'tooltip-class',
              tip: tips.email,
              required: true
            }"
            type="email"
            :class="{'error-highlight': inputRequireHighlight && !adminAccount.username}"
            :disabled="disableUpdateMasterAccount"
            :value="adminAccount.username"
            @change="event => {
              adminAccount.username = event.target.value
              updateAdminAccount()
            }"
          >
        </div>
      </div>
      <div
        v-if="!disableOpeningHourInEdit"
        class="tenancy-working-hours"
      >
        <label>{{ showHolidayHour ? $t('tenancy.regularOpeningHours'): $t('tenancy.openingHours') }}</label>
        <WorkingHours
          :hideLabel="true"
          :checkTimeValidate="false"
          :disabled="disableUpdate"
          :workingHours="openingHours.regularWorkingHours || []"
          :updateValueByPath="updateOpeningHours"
          :fieldPath="['regularWorkingHours']"
        />
        <template v-if="showHolidayHour">
          <label>{{ $t('tenancy.holidayOpeningHours') }}</label>
          <HolidayOpeningHours
            :hideLabel="true"
            :workingHours="openingHours.holidayWorkingHours || []"
            :updateValueByPath="updateOpeningHours"
            :fieldPath="['holidayWorkingHours']"
          />
        </template>
      </div>

      <div class="alarm-section">
        <div class="alarm-section__label-btn">
          <label>{{ $t('tenancy.alarmSysHint') }}</label>
        </div>

        <div class="fire-alarm-section">
          <label class="emphasis alarm-label">{{ $t('tenancy.fireAlarmSystem') }}:</label>
          <input
            v-hkldinput
            class="alarm-checkbox"
            :disabled="disableUpdate"
            type="radio"
            name="fireAlarm"
            :checked="currentTenancy.isFireAlarm"
            @change="updatePathField('isFireAlarm', true)"
          >
          <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
          <input
            v-hkldinput
            class="alarm-checkbox"
            :disabled="disableUpdate"
            type="radio"
            name="fireAlarm"
            :checked="![null, undefined].includes(currentTenancy.isFireAlarm) && !currentTenancy.isFireAlarm"
            @change="updatePathField('isFireAlarm', false)"
          >
          <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
        </div>
        <div>
          <label class="emphasis alarm-label">{{ $t('tenancy.burglarAlarmSystem') }}:</label>
          <input
            v-hkldinput
            class="alarm-checkbox"
            :disabled="disableUpdate"
            type="radio"
            name="burglarAlarm"
            :checked="currentTenancy.isBurglarAlarm"
            @change="updatePathField('isBurglarAlarm', true)"
          >
          <label class="alarm-checkbox-label">{{ $t('action.yes') }}</label>
          <input
            v-hkldinput
            class="alarm-checkbox"
            :disabled="disableUpdate"
            type="radio"
            name="burglarAlarm"
            :checked="![null, undefined].includes(currentTenancy.isBurglarAlarm) && !currentTenancy.isBurglarAlarm"
            @change="updatePathField('isBurglarAlarm', false)"
          >
          <label class="alarm-checkbox-label">{{ $t('action.no') }}</label>
        </div>
      </div>

      <hr>
    </div>
    <div class="premise-floor">
      <div class="row">
        <div
          class="input-set building-selector small-3 medium-3 large-3 columns"
          :class="{'required-error-highlight': inputRequireHighlight && !currentTenancy.building}"
        >
          <label class="emphasis required-flag">{{ $t('tenancy.building') }}</label>
          <HKLSelect
            v-hkldinput
            class="seacrh__building-selector"
            :disabled="disableUpdate || (!allowLinkContacts && disableBuildingUpdate)"
            :value="currentTenancy.buildingId || undefined"
            :options="buildingHklOptions"
            :showSearch="true"
            placeholder=""
            :fusionInputCheckPattern="bulidingSelectorValidateFunc"
            :required="true"
            :showTip="false"
            @change="onBuildingChange"
          />
        </div>
        <div class="disabled-input-wrapper small-3 medium-3 large-3 columns">
          <input
            id="office"
            v-hkldinput
            :disabled="!acTenancyIsOffice || disableUpdate"
            name="type"
            type="radio"
            class="radioClass"
            :checked="isOfficeType"
            @change="event =>updatePathField('type', tenancyType.Office)"
          >
          <label
            for="office"
            :class="{'text-bold': isOfficeType}"
          >
            {{ $t('tenancy.office') }}
          </label>
          <input
            id="retail"
            v-hkldinput
            :disabled="!acTenancyIsRetail || disableUpdate"
            name="type"
            type="radio"
            class="radioClass"
            :checked="!isOfficeType"
            @change="event =>updatePathField('type', tenancyType.Retail)"
          >
          <label
            for="retail"
            :class="{'text-bold': !isOfficeType}"
          >
            {{ $t('tenancy.retail') }}
          </label>
        </div>
        <div class="input-set building">
          <div
            class="search-zone"
            :class="{'required-error-highlight': zoneValidationFailed}"
          >
            <label
              class="emphasis"
              :class="{'required-flag': zoneFieldRequired}"
            >
              {{ $t('tenancy.zone') }}
            </label>
            <HKLMultiSelect
              v-hkldinput
              class="search-zone-selector"
              :class="{'error-highlight': zoneValidationFailed}"
              :disabled="!zoneOptions || zoneOptions.length <= 1 || disableUpdate"
              :value="zoneMultiValue"
              :options="zoneHklOptions"
              :dropdownMatchSelectWidth="false"
              placeholder=""
              @change="onZonesChange"
            />
          </div>
        </div>
        <div
          v-if="!isTenant && isCurrentRegionHK"
          class="e-key building"
        >
          <input
            v-model="ekeyEnabled"
            type="checkbox"
            :disabled="disableUpdate"
          >
          <label
            for="eKey"
            class="emphasis"
          >
            {{ $t('tenancy.pm-t006') }}
          </label>
          <TextField
            v-if="ekeyEnabled"
            :readonly="disableUpdate"
            :value="currentTenancy.eKey"
            @updateValue="value => updatePathField('eKey',value)"
          />
        </div>
      </div>
      <TextField
        class="all-linked-premises"
        :label="$t('tenancy.premises')"
        :value="premises"
        :showColon="false"
        :readonly="true"
      />
      <div
        v-for="(info, index) in premisesDetailList"
        :key="index + timeStamp"
        class="row premise-detail"
      >
        <div class="input-set detail small-3 medium-3 large-3 columns">
          <label
            v-if="!index"
            class="emphasis required-flag"
          >
            {{ $t('tenancy.premisedetail') }}
          </label>
          <TextField
            :readonly="disableUpdate"
            :readonlyAsInput="disableUpdate"
            :showColon="false"
            :value="info.detail || ''"
            @updateValue="val => editPremiseInfo('detail', index, val)"
          />
        </div>
        <div class="input-set floor small-3 medium-3 large-3 columns">
          <label
            v-if="!index"
            class="emphasis required-flag"
          >
            {{ $t('tenancy.floor') }}
          </label>
          <HKLSelect
            v-hkldinput
            :disabled="disableUpdate || (!allowLinkContacts && premiseCanBeUpdated(info))"
            :value="info.floor || undefined"
            :options="floorHklOptions"
            :showSearch="true"
            placeholder=""
            @change="val => editPremiseInfo('floor', index, val)"
          />
        </div>
        <div class="effectiveDate small-2 medium-2 large-2 columns">
          <label
            v-if="!index"
            class="emphasis required-flag"
          >
            {{ $t('tenancy.effectiveDate') }}
          </label>
          <DatePicker
            class="columns"
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="formatDate(info.effectiveDate)"
            placeholder=""
            :disabledPicker="disableUpdate"
            :disabled="{from: info.endDate ? new Date(info.endDate) : undefined}"
            @selected="val => editPremiseInfo('effectiveDate', index, val)"
          />
        </div>
        <div
          v-if="!isCreate"
          class="end-date small-2 medium-2 large-2 columns"
        >
          <label
            v-if="!index"
            class="emphasis"
          >
            {{ $t('tenancy.endDate') }}
          </label>
          <DatePicker
            class="columns"
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="formatDate(info.endDate)"
            placeholder=""
            :disabledPicker="disableUpdate"
            :disabled="{to: info.effectiveDate ? new Date(info.effectiveDate) : undefined}"
            @selected="val => editPremiseInfo('endDate', index, val)"
          />
        </div>
        <div
          v-if="!(disableUpdate || (!allowLinkContacts && premiseCanBeUpdated(info)))"
          class="premise-detail__actions columns end"
          :class="isCreate ? 'small-4 medium-4 large-4' : 'small-2 medium-2 large-2'"
        >
          <label
            v-if="!index"
            class="emphasis premise-detail__actions-label-spacer"
            aria-hidden="true"
          >&nbsp;</label>
          <span
            class="premise-detail__delete-button"
            @click="openDeleteConfirmModel(index)"
          >
            <i>
              <span
                class="fa fa-times fa-lg"
                aria-hidden="true"
              />
            </i>
          </span>
        </div>
      </div>
      <i
        v-if="!disableUpdate"
        class="add-premise-detail"
        @click="addPremiseInfo"
      >
        {{ $t('tenancy.addNewPremisesFloorDetail') }}
      </i>
    </div>
    <DeleteConfirmModel
      ref="deletePremiseInfoConfirmModel"
      :deleteFunction="deletePremiseInfo"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import DeleteConfirmModel from '@/controls/DeleteConfirmModel'
import DatePicker from '@/controls/DatePicker'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import mutationTypes from '@/store/mutation-types'
import TextField from '@/controls/TextField'
import WorkingHours from '@/controls/WorkingHours'
import HolidayOpeningHours from '@/modules/ccd/tenancy/HolidayOpeningHours'
import formFullnameMixin from '@/pages/formFullnameMixin'
import * as companiesApi from '@/api/companies/request'
import { getBuildings } from '@/api/buildings/request'
import * as regex from '@/utils/regex'
import dateUtil from '@/utils/dateUtil'
import { companyType, tenancyType } from '@/utils/locale'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import notification from '@/utils/notification'
import { SITE_TYPE } from '@/constants/modeltypes'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { regionNames } from '@/utils/consts'
import { concatLeasesAsPremises } from '@/modules/ccd/tenancy/utils'

const metaPremiseItem = {
  detail: '',
  floor: '',
  effectiveDate: '',
  endDate: '',
}

function sanitizeSections (sections) {
  if (!Array.isArray(sections)) return []
  return sections.filter(section => section && section !== '--')
}

export default {
  name: 'TenancyInformation',
  components: {
    TextField,
    HKLSelect,
    HKLMultiSelect,
    WorkingHours,
    DatePicker,
    DeleteConfirmModel,
    HolidayOpeningHours,
  },
  mixins: [formFullnameMixin],
  props: {
    company: Object,
    isCreate: Boolean,
    allowEdit: Boolean,
    allowUpdateMasterAccount: Boolean,
    companyId: [String, Number],
    allowLinkContacts: Boolean,
    originLeases: {
      type: Array,
      default: () => [],
    },
    originPremises: String,
  },
  data () {
    const tips = {
      email: this.$i18n.t('hint.inputTip.email'),
      phone: this.$i18n.t('hint.inputTip.phone'),
      string: this.$i18n.t('hint.inputTip.string'),
    }
    return {
      deep,
      searchTimeout: 500,
      buildingOptions: [],
      tradeNameOptions: [''],
      inputRequireHighlight: false,
      workingHours: {},
      searchCompaniesResult: [],
      companyNameSearchText: '',
      selectedCompany: null,
      changedType: '',
      tenancyType,
      regex,
      tips,
      premisesDetailListValidateResult: true,
      timeStamp: new Date(),
      companyType,
      ekeyEnabled: false,
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['currentUser', 'acTenancyIsRetail', 'acTenancyIsOffice', 'isTenant']),
    ...mapGetters('regions', ['backEndRegionalEnv', 'currentRegion']),

    isCurrentRegionHK () {
      return this.currentRegion.name === regionNames.HK
    },
    isHolidayModuleOpen () {
      return lodash.get(this.backEndRegionalEnv, 'moduleSwitch.holidayWorkingHours')
    },
    showHolidayHour () {
      return this.isHolidayModuleOpen && !this.isOfficeType
    },
    disableOpeningHourInEdit () {
      return this.showHolidayHour && !this.isCreate
    },
    premisesDetailList () {
      $('.premise-detail input').removeClass('error-highlight')
      $('.premise-detail textarea').removeClass('error-highlight')
      const buildingFloors = lodash.get(this.currentTenancy, 'building.floor', [])
      const list = lodash.cloneDeep(lodash.get(this.currentTenancy, 'premisesDetailList', []))
      this.timeStamp = new Date()
      return lodash.sortBy(list, item => {
        if (!item.floor) {
          return buildingFloors.length + 1
        }
        return buildingFloors.indexOf(item.floor)
      })
    },
    disableBuildingUpdate () {
      return !!this.eCircularContactsFloors.length
    },
    eCircularContactsFloors () {
      if (!(this.currentTenancy && this.currentTenancy.tenancyContacts)) return []
      const floors = lodash.uniq(this.currentTenancy.tenancyContacts
        .filter(item => item.CompanyContact.isCircular)
        .map(ecircularContact => ecircularContact.CompanyContact.cFloor)
        .reduce((floorList, floor) => floorList.concat(floor), []))
      return lodash.sortBy(floors)
    },
    zoneOptions () {
      return lodash.get(this.currentTenancy, 'building.zones', [])
    },
    openingHours () {
      return this.currentTenancy.openingHours || {}
    },
    sections () {
      return sanitizeSections(this.currentTenancy.sections)
    },
    zoneFieldRequired () {
      return !this.isTenant && this.zoneOptions.length > 0
    },
    zoneValidationFailed () {
      return this.inputRequireHighlight && this.zoneFieldRequired && !this.hasValidSections
    },
    hasValidSections () {
      if (!this.zoneFieldRequired) return true
      return sanitizeSections(this.currentTenancy.sections).length > 0
    },
    disableUpdate () {
      return !this.isCreate && !this.allowEdit
    },
    disableUpdateMasterAccount () {
      return !this.isCreate && !this.allowUpdateMasterAccount
    },
    floorOptions () {
      return lodash.get(this.currentTenancy, 'building.floor', [])
    },
    companyIdHklOptions () {
      return (this.companyIds || []).map((id) => ({ name: String(id), value: id }))
    },
    companyIdDisplayValue () {
      return this.isDummyCompany ? '--' : (this.currentTenancy.companyId || undefined)
    },
    companyNameHklOptions () {
      const options = (this.companyNames || []).map((name) => ({ name, value: name }))
      const temporaryCompanyNameOption = this.getTemporaryCompanyNameOption()
      return temporaryCompanyNameOption ? [temporaryCompanyNameOption, ...options] : options
    },
    tradeNameHklOptions () {
      return (this.tradeNameOptions || []).map((name) => ({ name, value: name }))
    },
    buildingHklOptions () {
      return (this.buildingOptions || []).map((building) => ({
        name: building.code,
        value: building.id,
      }))
    },
    zoneHklOptions () {
      return (this.zoneOptions || []).map((zone) => ({ name: zone, value: zone }))
    },
    zoneMultiValue () {
      const sections = this.currentTenancy.sections || []
      return sections.filter((zone) => zone !== '--')
    },
    floorHklOptions () {
      return (this.floorOptions || []).map((floor) => ({ name: floor, value: floor }))
    },
    premises () {
      const concatPremises = concatLeasesAsPremises(this.currentTenancy) || this.$t('emptyValue')
      return concatPremises
    },
    companyIds () {
      return this.searchCompaniesResult.map(company => company.id)
    },
    companyNames () {
      return this.searchCompaniesResult.map(company => `${company.name}(${company.id})`)
    },
    companyName () {
      return deep(this.currentTenancy, 'company.name')
    },
    canChangeCompanyNameAndID () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.changeCompanyName)
    },
    isDummyCompany () {
      return deep(this.currentTenancy, 'company.type') === companyType.D
    },
    isOfficeType () {
      return this.currentTenancy.type === tenancyType.Office
    },
    adminAccount () {
      if (this.currentTenancy && this.currentTenancy.accounts) {
        const account = this.currentTenancy.accounts.find(account => {
          return deep(account, 'TenancyAccount.isAdmin')
        })
        if (account) return JSON.parse(JSON.stringify(account))
        else return null
      }
    },
  },
  watch: {
    premisesDetailList () {
      if (!this.premisesDetailListValidateResult) {
        this.validatePremiseDetails()
      }
    },
  },
  async mounted () {
    this.ekeyEnabled = !!this.currentTenancy.eKey
    this.buildingOptions = await this.getBulidingOptions()
    // Because the currentTeancy.building doesn't have site info
    if (this.currentTenancy.building) {
      this.updateCurrentTenancyBuilding()
      this.selectBuilding(this.currentTenancy.building)
    }
    const tenancyLeases = deep(this.currentTenancy, 'company.leases') || []
    if (this.company) {
      this.selectedCompany = lodash.cloneDeep(this.company)
      this.setupTradeOptions(this.company.leases)
    } else {
      this.setupTradeOptions(tenancyLeases)
    }
  },
  methods: {
    ...mapActions('user', ['reloadUser']),
    ...mapMutations('ccd', {
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_PATH_FIELD,
      updateOpeningHours: mutationTypes.TENANCY_UPDATE_OPENING_HOURS,
    }),
    getTemporaryCompanyNameOption () {
      const searchText = String(this.companyNameSearchText || '').trim()
      if (!searchText) return null
      const normalizedSearch = searchText.toLowerCase()
      const exists = (this.companyNames || []).some((name) =>
        String(name).trim().toLowerCase() === normalizedSearch,
      )
      if (exists) return null
      return {
        name: searchText,
        value: searchText,
        isSingleTaggableInputTag: true,
        hklSelectHint: this.$t('hint.addTempCompany'),
      }
    },
    addDummyCompany (companyName) {
      this.selectedCompany = {
        name: companyName.trim(),
        type: companyType.D,
      }
      this.changeSelectReset(!this.isCreate)
    },
    selectBuilding (val) {
      const building = lodash.get(this.currentTenancy, 'building', '')
      const buildingChanged = !building || building.id !== val.id
      if (buildingChanged) {
        this.premisesDetailListFloorReset()
      }
      this.updatePathField('buildingCode', val.code)
      this.updatePathField('building', val)
      this.updatePathField('buildingId', val.id)
      if (buildingChanged) {
        const zones = val.zones || []
        const sections = zones.length === 1 ? [zones[0]] : []
        this.updatePathField('sections', sections)
      }
    },
    selectZone (value) {
      if (!value || value === '--') return
      const zones = sanitizeSections(lodash.cloneDeep(this.currentTenancy.sections))
      if (!zones.includes(value)) {
        zones.push(value)
      }
      this.updatePathField('sections', zones.sort())
    },
    removeZone (value) {
      let zones = sanitizeSections(lodash.cloneDeep(this.currentTenancy.sections))
      const index = zones.indexOf(value)
      if (index >= 0) {
        zones.splice(index, 1)
      }
      this.updatePathField('sections', zones.sort())
    },
    updatePathField (path, val) {
      this.updateTenancyPathField({
        path: [path],
        newValue: val,
      })
    },
    async searchCompanies (query) {
      query.type = companyType.T
      query.active = true
      this.searchCompaniesResult = await companiesApi.searchCompanies(query)
    },
    onCompanyNameSearch (value) {
      this.companyNameSearchText = value || ''
      return this.searchCompanies({ name: value })
    },
    onCompanyNameInput (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.clearCompanySelection()
        return
      }
      if (this.isDummyCompany) {
        this.selectedCompany = {
          ...lodash.cloneDeep(this.currentTenancy.company),
          name,
        }
        this.updatePathField('company', this.selectedCompany)
        return
      }
      this.selectedCompany = {
        name,
        type: companyType.D,
      }
      this.changeSelectReset(!this.isCreate)
    },
    updateCompanyPathField (path, val) {
      this.updateTenancyPathField({
        path: ['company', path],
        newValue: val,
      })
    },
    selectCompanyId (id) {
      this.searchCompaniesResult.forEach(company => {
        if (company.id === id) {
          this.selectedCompany = company
        }
      })
      this.changeSelectReset(true)
    },
    selectCompanyName (name) {
      this.searchCompaniesResult.forEach(company => {
        if (`${company.name}(${company.id})` === name) {
          this.selectedCompany = company
        }
      })
      this.changeSelectReset(true)
      this.updatePathField('companyChangeNotSaved', true)
    },
    onCompanyNameChange (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.clearCompanySelection()
        return
      }
      this.companyNameSearchText = ''
      const formatted = this.companyNames.find((option) => option === name)
      if (formatted) {
        this.selectCompanyName(name)
      } else {
        this.addDummyCompany(name)
      }
    },
    clearCompanySelection () {
      this.companyNameSearchText = ''
      this.selectedCompany = null
      this.updatePathField('companyId', '')
      this.updatePathField('company', {})
      this.updatePathField('leases', lodash.cloneDeep(this.originLeases))
      this.updatePathField('premises', lodash.cloneDeep(this.originPremises))
      this.updatePathField('floor', [])
      this.updatePathField('tradeName', '')
      this.tradeNameOptions = []
    },
    onTradeNameChange (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      this.updatePathField('tradeName', name ? name.trim() : '')
    },
    onTradeNameInput (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      this.updatePathField('tradeName', name || '')
    },
    onBuildingChange (buildingId) {
      const building = this.buildingOptions.find((item) => item.id === buildingId)
      if (building) {
        this.selectBuilding(building)
      }
    },
    onZonesChange (zones) {
      this.updatePathField('sections', (zones || []).sort())
    },
    setupTradeOptions (leases) {
      if (this.isDummyCompany) {
        this.tradeNameOptions = []
      } else if (leases) {
        this.tradeNameOptions = leases.reduce((result, item) => {
          if (!result.includes(item.tradeName)) {
            result.push(item.tradeName)
          }
          return result.sort()
        }, [])
      }
    },
    changeSelectReset (resetTradeName = false) {
      this.updatePathField('companyId', this.selectedCompany.id)
      this.updatePathField('company', this.selectedCompany)
      this.updatePathField('leases', lodash.cloneDeep(this.originLeases))
      this.updatePathField('premises', lodash.cloneDeep(this.originPremises))
      this.updatePathField('floor', [])
      if (resetTradeName) {
        this.updatePathField('tradeName', '')
      }
      this.setupTradeOptions(this.selectedCompany.leases)
    },
    isSamePhoneNumbers () {
      if (!this.currentTenancy.id) return this.currentTenancy.phone && this.currentTenancy.phone !== '' && this.currentTenancy.phone === this.currentTenancy.altPhone
      if (this.adminAccount && this.adminAccount.TenancyAccount) return this.adminAccount.TenancyAccount.phone && this.adminAccount.TenancyAccount.phone !== '' && this.adminAccount.TenancyAccount.phone === this.adminAccount.TenancyAccount.altPhone
      return false
    },
    isHadphysicalSite (buliding) {
      const result = buliding.sites.some(item => SITE_TYPE.PHYSICAL)
      return result
    },
    bulidingSelectorValidateFunc (value) {
      let bool = false
      const building = this.buildingOptions.find(item => item.id === value)
      if (building) {
        bool = this.isHadphysicalSite(building)
        if (!bool) {
          notification.error(this.$t('hint.invalidBuildingForTenancy'))
        }
      }
      return bool
    },
    bulidingValidator () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'}, '.building-selector')
      return fusionInputCheckUtil.isAllFieldsValid('.building-selector')
    },
    createValidator () {
      let isValid = true
      this.inputRequireHighlight = false
      fusionInputCheckUtil.resetAllFields()
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'}, '.main__contact-email')
      if ((!this.currentTenancy.companyId && !this.isDummyCompany) || !this.currentTenancy.firstName ||
        !this.currentTenancy.lastName || !this.currentTenancy.email || !this.currentTenancy.building ||
        !this.currentTenancy.tradeName || !this.hasValidSections) {
        this.inputRequireHighlight = true
        isValid = false
      }
      this.premisesDetailListValidateResult = this.validatePremiseDetails()
      if (!this.premisesDetailListValidateResult) {
        isValid = false
      }
      if (this.isSamePhoneNumbers()) {
        notification.fireNotification(false, this.$i18n.t('hint.inputTip.phoneShouldBeDifferent'))
        isValid = false
        this.inputRequireHighlight = true
      }
      const isEmailValidation = fusionInputCheckUtil.isAllFieldsValid('.main__contact-email')
      const isBuildingValidation = this.bulidingValidator()
      return isValid && $('.main__contact-email input.is-invalid-input').length === 0 &&
        isEmailValidation && isBuildingValidation
    },
    saveValidator () {
      let isValid = true
      this.inputRequireHighlight = false
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'}, '.main__contact-email')
      if (!deep(this.adminAccount, 'TenancyAccount.contactFirstName') ||
        !deep(this.adminAccount, 'TenancyAccount.contactLastName') ||
        !deep(this.adminAccount, 'username') ||
        !this.currentTenancy.tradeName || !this.hasValidSections) {
        this.inputRequireHighlight = true
        isValid = false
      }

      this.premisesDetailListValidateResult = this.validatePremiseDetails()
      if (!this.premisesDetailListValidateResult) {
        isValid = false
      }
      if (this.isSamePhoneNumbers()) {
        notification.fireNotification(false, this.$i18n.t('hint.inputTip.phoneShouldBeDifferent'))
        isValid = false
        this.inputRequireHighlight = true
      }
      const isBuildingValidation = this.bulidingValidator()
      return isValid && $('.main__contact-email input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.main__contact-email') && isBuildingValidation
    },
    validatePremiseDetails () {
      let isValidate = true
      $('.premise-detail input').removeClass('error-highlight')
      $('.premise-detail textarea').removeClass('error-highlight')
      const infos = this.premisesDetailList
      const mandatoryFields = ['detail', 'floor', 'effectiveDate']
      for (let i = 0, len = infos.length; i < len; i++) {
        mandatoryFields.forEach(key => {
          if (!infos[i][key]) {
            $($(`.premise-detail .${key} input`)[i]).addClass('error-highlight')
            $($(`.premise-detail .${key} textarea`)[i]).addClass('error-highlight')
            isValidate = false
          }
        })
        for (let j = i + 1; j < len; j++) {
          if (infos[i].detail === infos[j].detail && infos[i].floor === infos[j].floor) {
            $($(`.premise-detail .detail input`)[i]).addClass('error-highlight')
            $($(`.premise-detail .floor input`)[i]).addClass('error-highlight')
            $($(`.premise-detail .detail input`)[j]).addClass('error-highlight')
            $($(`.premise-detail .floor input`)[j]).addClass('error-highlight')
            isValidate = false
          }
        }
      }
      return isValidate
    },
    removeItemFromFloor (val) {
      const list = lodash.cloneDeep(this.currentTenancy.floor) || []
      const index = list.indexOf(val)
      if (index !== -1) {
        list.splice(index, 1)
        this.updatePathField('floor', list)
        this.refreshZone(list)
      }
    },
    addItemToFloor (val) {
      const list = lodash.cloneDeep(this.currentTenancy.floor) || []
      if (!list.includes(val)) {
        list.push(val)
        this.updatePathField('floor', list.sort())
        this.addZones(val)
      }
    },
    addZones (floor) {
      const zones = this.getZones(this.currentTenancy.building, floor)
      const list = lodash.cloneDeep(this.currentTenancy.zone) || []
      zones.forEach(zone => {
        if (zone && !list.includes(zones)) {
          list.push(zone)
        }
      })
      this.updatePathField('zone', list)
    },
    refreshZone (floors) {
      const zoneSet = new Set()
      floors.forEach(floor => {
        const zones = this.getZones(this.currentTenancy.building, floor)
        zones.forEach(zone => {
          zoneSet.add(zone)
        })
      })
      this.updatePathField('zone', Array.from(zoneSet))
    },
    getZones (floor) {
      const floorMap = lodash.get(this.currentTenancy, 'building.floorMap', [])
      const zones = []
      if (floorMap.L && floorMap.L.includes(floor)) {
        zones.push('L')
      }
      if (floorMap.M && floorMap.M.includes(floor)) {
        zones.push('M')
      }
      if (floorMap.H && floorMap.H.includes(floor)) {
        zones.push('H')
      }
      return zones
    },
    updateAdminAccount () {
      if (this.currentTenancy && this.currentTenancy.accounts) {
        const index = this.currentTenancy.accounts.findIndex(account => {
          return deep(account, 'TenancyAccount.isAdmin')
        })
        if (index >= 0) {
          this.updateTenancyPathField({
            path: ['accounts', index],
            newValue: this.adminAccount,
          })
        }
      }
    },
    editPremiseInfo (path, index, val) {
      const info = lodash.cloneDeep(this.premisesDetailList)
      info[index][path] = val
      this.updatePathField('premisesDetailList', info)
      formUtils.setDirty()
    },
    addPremiseInfo () {
      const premisesDetailList = lodash.cloneDeep(lodash.get(this.currentTenancy, 'premisesDetailList', []))
      premisesDetailList.push(metaPremiseItem)
      this.updatePathField('premisesDetailList', premisesDetailList)
    },
    openDeleteConfirmModel (index) {
      this.$refs.deletePremiseInfoConfirmModel.openModal(err => {
        if (!err) {
          this.deletePremiseInfo(index)
        }
      })
    },
    deletePremiseInfo (index) {
      const premisesDetailList = lodash.cloneDeep(this.premisesDetailList)
      premisesDetailList.splice(index, 1)
      this.updatePathField('premisesDetailList', premisesDetailList)
    },
    formatDate (val) {
      return dateUtil.formatDate(val)
    },
    premisesDetailListFloorReset () {
      const premisesDetailList = lodash.cloneDeep(lodash.get(this.currentTenancy, 'premisesDetailList', []))
      premisesDetailList.map(list => {
        list.floor = ''
      })
      this.updatePathField('premisesDetailList', premisesDetailList)
    },
    premiseCanBeUpdated (premise) {
      return this.eCircularContactsFloors.includes(premise.floor)
    },
    async getBulidingOptions () {
      const buildingIds = this.currentUser.buildings.map(item => item.id)
      const result = await getBuildings({ '~id': buildingIds.join(',') })
      return result
    },
    updateCurrentTenancyBuilding () {
      const result = this.buildingOptions.filter(item => item.id === this.currentTenancy.buildingId)
      if (result[0]) {
        this.updatePathField('building', result[0])
      } else {
        notification.warn(this.$t('missingBuildingPermission'))
      }
    },
  },
}
</script>

<style lang="scss">
@import '~styles/multi-value';
.tenancy-info-wrapper {
  .search-zone {
    padding-right: 80px;
    .search-zone-selector {
      height: 40px;
      min-height: 40px;
      min-width: 107px;
    }
    .search-zone-selector.hkl-select,
    .search-zone-selector .hkl-select {
      min-height: 40px;
      min-width: 107px;
    }
    .multiselect {
      .multiselect__select {
        position: relative;
        float: right;
      }
      .multiselect_tags-wrap {
        float: left;
        .multiselect__tags {
          width: 100%;
        }
      }
      .multiselect__tag {
        background: #6e7a91;
        height: 38px;
        margin-bottom: 0;
        line-height: 30px;

        .multiselect__tag-icon::after {
          color: #3a505f;
          line-height: 33px;
        }
        .multiselect__tag-icon:hover {
          background: #6e7a91;
        }
      }
    }
  }

  span.multiselect__option.multiselect__option--highlight::after {
    height: 36px;
  }
  .premise-floor {
    textarea {
      width: 100% !important;
    }
  }
  .building-selector label {
    margin-top: 0;
  }

  input {
    box-shadow: none;
  }
  input:focus {
    outline: none;
  }

  .vdp-datepicker--disabled {
    opacity: 1 !important;
  }
  .all-linked-premises.text-field label {
    line-height: 1;
  }
}
</style>

<style lang="scss" scoped>
@import '~styles/tender/view';

.tenancy-info-wrapper {
  .required-flag:after {
    content: '*';
  }
  .company-info-sec, .premises-info-sec {
    padding-bottom: 15px;
    .company-info {
      label {
        margin-top: 0px;
      }
    }
    .text-input textarea{
      width: 100%;
    }
  }
  .premises-info-sec {
    padding-bottom: 0;
  }
  .input-set {
    padding-right: 20px;
  }
  .e-key{
    width: 325px;
    textarea {
      color: #0C3A58;
      font-size: 14px;
    }
  }

  .disabled-input-wrapper {
    align-items: center;
    padding-top: 44px;
    .radioClass {
      width: 20px;
      height: 20px;
      margin: 0;
    }
    .text-bold {
      font-weight: bold;
    }
    label {
      margin: 0 55px 0 12px;
      font-weight: normal;
    }
  }
  .multiselect {
    width: 100%;
  }

  .search__company {
    position: relative;
    .hkl-select {
      width: 100%;

      ::v-deep .ant-select-selection {
        border: solid 1px #B5B5B5;
        box-sizing: border-box;
      }

      ::v-deep .ant-select-selection--single,
      ::v-deep .ant-select-selection--multiple {
        min-height: 40px;
      }

      ::v-deep .ant-select-selection__choice {
        margin-bottom: 0;
        text-transform: none;
      }
    }

    & input {
      text-emphasis: 30px;
      width: 100%;
    }
    & .fa-search {
      position: absolute;
      top: 8px;
      left: 11px;
      font-size: 23px;
      color: #B5B5B5;
      opacity: 1;
    }

    & input:not([value=""]) + span {
      display: none;
    }

    .hkl-select:focus-within + .fa-search,
    .hkl-select.ant-select-open + .fa-search,
    .hkl-select.ant-select-focused + .fa-search {
      display: none;
    }
  }
  .search__company--id .seacrh__company-selector {
    ::v-deep .ant-select-selection__rendered {
      margin-right: 0;
    }

    ::v-deep .ant-select-arrow {
      display: none !important;
    }
  }
  .search__company--name .seacrh__company-selector {
    ::v-deep .ant-select-selection__choice {
      background: transparent !important;
      color: #35495E !important;
      text-transform: none !important;
      font-weight: normal !important;
      margin: 0 !important;
      padding: 0 4px !important;
      border: none !important;
    }

    ::v-deep .ant-select-selection__choice__remove {
      display: none !important;
    }
  }
  .seacrh__company-selector {
    width: 100%;
    & .multiselect__tags {
      width: 100%;
    }
    & .multiselect__select {
      width: 0;
      padding: 0;
      &:before {
        content: ''
      }
    }
  }
  .u-medium-margin-right {
    margin-right: 1.4%;
  }
  .main__contact-name {
    & input:first-child {
      border-right: none;
    }
  }
  .main__contact-email {
    input {
      color: #0C3A58;
      font-size: 14px;
    }
  }

  .tenancy-working-hours {
    width: 59%;
    .working-hours-add .multiselect + .multiselect {
      margin-left: 13px;
    }
  }

  .alarm-section {
    .alarm-label {
      display: inline;
      margin-right: 10px;
      font-weight: normal;
    }
    .alarm-checkbox {
      margin-bottom: 5px;
    }
    .alarm-checkbox-label {
      margin: 0px;
      font-weight: normal;
    }
  }

  .premise-floor {
    .premise-detail label{
      margin-top: 0;
    }

    .premise-detail {
      margin-top: 5px;
      position: relative;
    }
    .effectiveDate {
      padding-right: 20px;
      .vdp-datepicker.columns {
        width: auto;
      }
    }

    .end-date {
      padding-right: 20px;
      .vdp-datepicker.columns {
        width: auto;
      }
    }
    .premise-detail__actions {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-right: 20px;
    }
    .premise-detail__actions-label-spacer {
      width: 100%;
    }

    .add-premise-detail {
      cursor: pointer;
      margin-top: 15px;
      display: inline-block;
      text-decoration: underline;
    }
    .premise-detail__delete-button {
      display: inline-block;
      margin: 10px 5px;
      cursor: pointer;
    }
    .all-linked-premises {
      margin-top: 18px;
    }
  }
}

input[type='text'], input[type='number'] {
  width: 100%;
}

.mock-read-only-textarea {
  border: 1px solid #b5b5b5;
  background-color: #e3e3e3;
  min-height: 40px;
  line-height: 1.15;
  padding: 0.5rem 0.5rem 0.5rem 10px;
}
</style>

<style lang="scss">
.tenancy-info-wrapper {
  .search__company {
    position: relative;
    .hkl-select {
      ::v-deep .ant-select-selection__select-arrow {
        right: 9px;
      }
      ::v-deep .ant-select-arrow {
        color: #35495e;
      }

      ::v-deep .ant-select-selection--single .ant-select-selection__rendered {
        margin-right: 0;
      }
    }

    .multiselect.seacrh__company-selector>.multiselect__select::before {
      display: none;
    }
  }
  .multiselect.seacrh__tradeName-selector>.multiselect__select::before {
      display: none;
  }
  .multiselect.seacrh__company-selector>.multiselect__select,
  .multiselect.seacrh__tradeName-selector>.multiselect__select {
    cursor: text;
  }
}
</style>
